// src/screens/LockSessionHireView.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Loader,
  Text,
  Title,
} from "@mantine/core";
import {
  CardElement,
  Elements,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  FaChevronLeft,
  FaClock,
  FaCreditCard,
  FaDollarSign,
  FaUser,
  FaMinus,
  FaPlus,
} from "react-icons/fa";
import { TimerWheelView } from "react-timer-wheel-kit";
import { DotNetAPI } from "../atoms/dotnetAPI";
const APP_CLIP_ACCENT = "#228be6";
const UI = {
  screen: {
    position: "relative",
    minHeight: "100dvh",
    overflow: "hidden",
    background:
      "linear-gradient(135deg, #ffffff 0%, #f1f3f5 48%, #ffffff 100%)",
  },
  content: {
    position: "relative",
    zIndex: 2,
    width: "100%",
  },
  shell: {
    width: "100%",
    maxWidth: 500,
    marginInline: "auto",
  },
  glassCard: {
    width: "100%",
    padding: 22,
    borderRadius: 30,
    background: "rgba(255, 255, 255, 0.72)",
    border: "1px solid rgba(255, 255, 255, 0.56)",
    boxShadow: "0 22px 54px rgba(15, 23, 42, 0.08)",
    backdropFilter: "blur(28px) saturate(150%)",
    WebkitBackdropFilter: "blur(28px) saturate(150%)",
  },
  innerCard: {
    width: "100%",
    padding: 18,
    borderRadius: 20,
    background: "rgba(255, 255, 255, 0.62)",
    border: "1px solid rgba(255, 255, 255, 0.54)",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.045)",
    backdropFilter: "blur(18px) saturate(145%)",
    WebkitBackdropFilter: "blur(18px) saturate(145%)",
  },
  backButton: {
    width: 48,
    height: 48,
    minWidth: 48,
    padding: 0,
    borderRadius: "50%",
    color: "#1f2937",
    background: "rgba(255, 255, 255, 0.70)",
    border: "1px solid rgba(255, 255, 255, 0.58)",
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.07)",
    backdropFilter: "blur(20px) saturate(145%)",
    WebkitBackdropFilter: "blur(20px) saturate(145%)",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 42,
    padding: "9px 14px",
    borderRadius: 999,
    color: "#667085",
    background: "rgba(255, 255, 255, 0.68)",
    border: "1px solid rgba(255, 255, 255, 0.56)",
    backdropFilter: "blur(18px) saturate(145%)",
    WebkitBackdropFilter: "blur(18px) saturate(145%)",
  },
  primaryButton: {
    height: 58,
    borderRadius: 18,
    fontWeight: 700,
    fontSize: 16,
    boxShadow: "none",
  },
  cardElement: {
    minHeight: 58,
    display: "flex",
    alignItems: "center",
    padding: "17px 18px",
    borderRadius: 20,
    background: "rgba(255, 255, 255, 0.76)",
    border: "1px solid rgba(255, 255, 255, 0.68)",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
  },
};
function AmbientBackground() {
  return (
    <Box
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      <style>{`
        @keyframes slb-hire-orb-one {
          from { transform: translate3d(-30px, -120px, 0); }
          to { transform: translate3d(-115px, -245px, 0); }
        }
        @keyframes slb-hire-orb-two {
          from { transform: translate3d(50px, 100px, 0); }
          to { transform: translate3d(135px, 225px, 0); }
        }
        @keyframes slb-hire-orb-three {
          from { transform: translate3d(30px, -190px, 0); }
          to { transform: translate3d(110px, -100px, 0); }
        }
        .slb-wallet-button {
          border-radius: 18px;
          overflow: hidden;
        }
        .slb-card-element .StripeElement {
          width: 100%;
        }
        @media (prefers-reduced-motion: reduce) {
          .slb-hire-orb {
            animation: none !important;
          }
        }
      `}</style>
      <Box
        className="slb-hire-orb"
        style={{
          position: "absolute",
          width: 340,
          height: 340,
          left: "7%",
          top: "6%",
          borderRadius: "50%",
          background: "rgba(34, 139, 230, 0.18)",
          filter: "blur(70px)",
          animation: "slb-hire-orb-one 8s ease-in-out infinite alternate",
        }}
      />
      <Box
        className="slb-hire-orb"
        style={{
          position: "absolute",
          width: 320,
          height: 320,
          right: "3%",
          bottom: "2%",
          borderRadius: "50%",
          background: "rgba(59, 130, 246, 0.12)",
          filter: "blur(70px)",
          animation: "slb-hire-orb-two 10s ease-in-out infinite alternate",
        }}
      />
      <Box
        className="slb-hire-orb"
        style={{
          position: "absolute",
          width: 230,
          height: 230,
          right: "18%",
          top: "10%",
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.78)",
          filter: "blur(55px)",
          animation: "slb-hire-orb-three 7s ease-in-out infinite alternate",
        }}
      />
    </Box>
  );
}
function truthy(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}
function extractPk(data) {
  if (!data || typeof data !== "object") {
    return null;
  }
  const tryList = (obj) =>
    [
      obj?.stripe_key,
      obj?.primaryKey,
      obj?.stripe_pk,
      obj?.stripePublishableKey,
      obj?.stripe_publishable_key,
      obj?.publishable_key,
      obj?.publishableKey,
      obj?.pk,
      obj?.key,
    ].filter(Boolean);
  const flat = tryList(data);
  const business = data.business || data.biz || data.company || {};
  const nestedBusiness = tryList(business);
  const nestedItem = tryList(data.item || {});
  return (
    [...flat, ...nestedBusiness, ...nestedItem].find(
      (value) => typeof value === "string" && /^pk_(test|live)_/.test(value),
    ) || null
  );
}
function toNumber(value, fallback = 0) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}
function formatCurrency(value) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}
function getApiMessage(data, fallback) {
  return data?.message || data?.error || fallback;
}
const cardElementOptions = {
  hidePostalCode: true,
  style: {
    base: {
      color: "#1f2937",
      fontSize: "17px",
      fontWeight: "500",
      lineHeight: "24px",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "::placeholder": {
        color: "#98a2b3",
      },
    },
    invalid: {
      color: "#e03131",
      iconColor: "#e03131",
    },
  },
};
export default function LockSessionHireView(props) {
  const { stripePublishableKey } = props;
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState("");
  useEffect(() => {
    let alive = true;
    async function initialiseStripe() {
      try {
        const pk =
          typeof stripePublishableKey === "string"
            ? stripePublishableKey
            : extractPk(stripePublishableKey);
        if (!pk || !/^pk_(test|live)_/.test(pk)) {
          throw new Error(
            "Stripe publishable key not provided for this business.",
          );
        }
        const promise = loadStripe(pk);
        if (alive) {
          setStripePromise(promise);
        }
      } catch (error) {
        if (alive) {
          setBootError(error?.message || "Failed to initialise payments.");
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }
    initialiseStripe();
    return () => {
      alive = false;
    };
  }, [stripePublishableKey]);
  if (bootError) {
    return (
      <Box style={UI.screen}>
        <AmbientBackground />
        <Container size={560} px="md" style={UI.content}>
          <Center
            style={{
              minHeight: "100dvh",
            }}
          >
            <Alert
              color="red"
              title="Payment unavailable"
              radius={20}
              style={{
                ...UI.glassCard,
                maxWidth: 500,
              }}
            >
              {bootError}
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }
  if (loading || !stripePromise) {
    return (
      <Box style={UI.screen}>
        <AmbientBackground />
        <Center
          style={{
            position: "relative",
            zIndex: 2,
            minHeight: "100dvh",
          }}
        >
          <Loader size="md" color={APP_CLIP_ACCENT} />
        </Center>
      </Box>
    );
  }
  return (
    <Elements stripe={stripePromise}>
      <LockSessionHireViewInner {...props} />
    </Elements>
  );
}
function LockSessionHireViewInner({
  itemID,
  item,
  site,
  weekdays = [],
  specificDates = [],
  sessionPurchaseAvailability = null,
  getLiveSessionPurchaseAvailability = null,
  phoneNumber,
  onPaymentSuccess,
  onCancel,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const chargePerSession = truthy(item?.charge_per_session);
  const endTillCloseTime = truthy(item?.end_til_close_time);
  const costBase = toNumber(item?.hire_cost_base, 0);
  const customerCostBase = toNumber(item?.hire_cost_base_person, 0);
  const hasCustomerCost = customerCostBase > 0;
  const [customerCount, setCustomerCount] = useState("1");
  const customerCountValue = Math.max(
    1,
    Math.floor(toNumber(customerCount, 1)),
  );
  const effectiveCostPerMinute =
    costBase + customerCostBase * customerCountValue;
  const maxMinutes = toNumber(item?.hire_max_length, 0);
  const itemTitle = item?.title || "Session";
  const currentSlotMinutes = Math.max(
    0,
    Math.floor(
      toNumber(
        sessionPurchaseAvailability?.currentSlotRemainingMinutes,
        0,
      ),
    ),
  );
  // Priority:
  // 1. end_til_close_time ALWAYS controls duration.
  // 2. charge_per_session controls pricing only.
  // 3. otherwise the user selects minutes and pays per minute.
  const fixedDurationMode =
    endTillCloseTime || chargePerSession;
  const fixedSessionMinutes = endTillCloseTime
    ? currentSlotMinutes
    : maxMinutes;
  const fixedSessionCost = chargePerSession
    ? costBase + customerCostBase * customerCountValue
    : fixedSessionMinutes * effectiveCostPerMinute;
  const fixedSessionTotalCents =
    Math.round(fixedSessionCost * 100);
  const fixedSessionReady =
    fixedDurationMode &&
    fixedSessionMinutes > 0 &&
    fixedSessionTotalCents > 0;
  const [walletAvailable, setWalletAvailable] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [walletType, setWalletType] = useState(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [hasSelection, setHasSelection] = useState(fixedSessionReady);
  const [selectedMinutes, setSelectedMinutes] = useState(
    fixedSessionReady ? fixedSessionMinutes : 0,
  );
  const [selectedCost, setSelectedCost] = useState(
    fixedSessionReady ? fixedSessionCost : 0,
  );
  const selectedMinutesRef = useRef(
    fixedSessionReady ? fixedSessionMinutes : 0,
  );
  const totalCentsRef = useRef(fixedSessionReady ? fixedSessionTotalCents : 0);
  const wheelDoneRef = useRef(null);
  const walletProcessingRef = useRef(false);
  const handlePaymentMethodRef = useRef(null);
  useEffect(() => {
    if (!fixedDurationMode) {
      return;
    }
    const ready =
      fixedSessionMinutes > 0 &&
      fixedSessionTotalCents > 0;
    selectedMinutesRef.current = ready
      ? fixedSessionMinutes
      : 0;
    totalCentsRef.current = ready
      ? fixedSessionTotalCents
      : 0;
    setSelectedMinutes(
      ready ? fixedSessionMinutes : 0,
    );
    setSelectedCost(
      ready ? fixedSessionCost : 0,
    );
    setHasSelection(ready);
  }, [
    fixedDurationMode,
    fixedSessionMinutes,
    fixedSessionCost,
    fixedSessionTotalCents,
  ]);
  useEffect(() => {
    if (fixedDurationMode || !hasCustomerCost) {
      return;
    }
    wheelDoneRef.current?.(false);
    wheelDoneRef.current = null;
    selectedMinutesRef.current = 0;
    totalCentsRef.current = 0;
    setSelectedMinutes(0);
    setSelectedCost(0);
    setHasSelection(false);
    setError("");
  }, [fixedDurationMode, customerCountValue, hasCustomerCost]);
  const createPaymentIntent = useCallback(
    async (minutes, totalCents) => {
      if (!itemID) {
        throw new Error("Item ID is missing.");
      }
      if (!phoneNumber) {
        throw new Error("Phone number is missing.");
      }
      const liveAvailability =
        typeof getLiveSessionPurchaseAvailability === "function"
          ? getLiveSessionPurchaseAvailability()
          : sessionPurchaseAvailability;
      const resolvedMinutes = endTillCloseTime
        ? Math.max(
            0,
            Math.floor(
              toNumber(
                liveAvailability?.currentSlotRemainingMinutes,
                0,
              ),
            ),
          )
        : minutes;
      if (resolvedMinutes <= 0) {
        throw new Error("Invalid hire length.");
      }
      const resolvedTotalCents = chargePerSession
        ? Math.round(
            (
              costBase +
              customerCostBase * customerCountValue
            ) * 100,
          )
        : endTillCloseTime
          ? Math.round(
              resolvedMinutes *
                effectiveCostPerMinute *
                100,
            )
          : totalCents;
      if (resolvedTotalCents <= 0) {
        throw new Error("Invalid payment amount.");
      }
      const response = await DotNetAPI.post(
        `hireable-items/${itemID}/payment`,
        {
          phone: phoneNumber,
          total: resolvedTotalCents,
          minutes: resolvedMinutes,
          customers: customerCountValue,
        },
      );
      const json = response?.data || {};
      if (json?.status !== true) {
        throw new Error(getApiMessage(json, "Failed to create payment."));
      }
      const data = json?.data || {};
      const intent = data?.intent?.data || {};
      const clientSecret =
        intent?.client_secret ||
        intent?.clientSecret ||
        data?.client_secret ||
        data?.clientSecret ||
        "";
      const transactionId = data?.transaction_id || data?.transactionId || null;
      if (!clientSecret) {
        console.error("Payment intent response:", json);
        throw new Error("Payment client secret was not returned.");
      }
      if (!transactionId) {
        throw new Error("Transaction ID was not returned.");
      }
      return {
        clientSecret,
        transactionId,
        resolvedMinutes,
        resolvedTotalCents,
      };
    },
    [
      itemID,
      phoneNumber,
      customerCountValue,
      endTillCloseTime,
      chargePerSession,
      costBase,
      customerCostBase,
      effectiveCostPerMinute,
      sessionPurchaseAvailability,
      getLiveSessionPurchaseAvailability,
    ],
  );
  const createSession = useCallback(
    async (transactionId, minutes) => {
      const response = await DotNetAPI.post(
        `hireable-items/${itemID}/transactions/${transactionId}/session`,
        {
          phone: phoneNumber,
          minutes,
          customers: customerCountValue,
        },
      );
      const json = response?.data || {};
      if (json?.status !== true) {
        throw new Error(
          getApiMessage(
            json,
            "Payment succeeded but the session could not be created.",
          ),
        );
      }
      const data = json?.data || {};
      return {
        sessionId: data?.session_id,
        length: data?.length || minutes,
      };
    },
    [itemID, phoneNumber, customerCountValue],
  );
  const completePayment = useCallback(
    async (transactionId, minutes, totalCents) => {
      const session = await createSession(transactionId, minutes);
      wheelDoneRef.current?.(true);
      await onPaymentSuccess?.({
        transactionId,
        sessionId: session.sessionId,
        length: session.length,
        minutes: session.length,
        totalCents,
        customers: customerCountValue,
      });
    },
    [createSession, customerCountValue, onPaymentSuccess],
  );
  const handlePaymentMethod = useCallback(
    async (event) => {
      if (walletProcessingRef.current) {
        return;
      }
      walletProcessingRef.current = true;
      setPaying(true);
      setError("");
      let eventCompleted = false;
      const completeWalletEvent = (result) => {
        if (eventCompleted) {
          return;
        }
        eventCompleted = true;
        try {
          event.complete(result);
        } catch (completeError) {
          console.warn(
            "Unable to complete Stripe wallet event:",
            completeError,
          );
        }
      };
      try {
        const minutes = selectedMinutesRef.current;
        const totalCents = totalCentsRef.current;
        if (minutes <= 0 || totalCents <= 0) {
          completeWalletEvent("fail");
          wheelDoneRef.current?.(false);
          setError("Please select your hire length before paying.");
          return;
        }
        console.log("[Wallet] Creating PaymentIntent", {
          minutes,
          totalCents,
        });
        const {
          clientSecret,
          transactionId,
          resolvedMinutes,
          resolvedTotalCents,
        } = await createPaymentIntent(
          minutes,
          totalCents,
        );
        console.log("[Wallet] Confirming payment", {
          transactionId,
          resolvedMinutes,
        });
        const firstConfirmation =
          await stripe.confirmCardPayment(
            clientSecret,
            {
              payment_method: event.paymentMethod.id,
            },
            {
              handleActions: false,
            },
          );
        if (firstConfirmation.error) {
          completeWalletEvent("fail");
          wheelDoneRef.current?.(false);
          setError(
            firstConfirmation.error?.message ||
              "Payment failed.",
          );
          return;
        }
        let finalPaymentIntent =
          firstConfirmation.paymentIntent;
        if (
          finalPaymentIntent?.status ===
          "requires_action"
        ) {
          console.log(
            "[Wallet] Additional payment authentication required",
          );
          const actionResult =
            await stripe.confirmCardPayment(
              clientSecret,
            );
          if (actionResult.error) {
            completeWalletEvent("fail");
            wheelDoneRef.current?.(false);
            setError(
              actionResult.error?.message ||
                "Payment authorisation failed.",
            );
            return;
          }
          finalPaymentIntent =
            actionResult.paymentIntent;
        }
        const finalStatus =
          finalPaymentIntent?.status || "";
        if (
          finalStatus !== "succeeded" &&
          finalStatus !== "processing" &&
          finalStatus !== "requires_capture"
        ) {
          throw new Error(
            `Payment was not completed. Stripe status: ${
              finalStatus || "unknown"
            }.`,
          );
        }
        console.log(
          "[Wallet] Payment confirmed. Creating hired session",
          {
            transactionId,
            resolvedMinutes,
            status: finalStatus,
          },
        );
        // Create the hired session BEFORE telling Apple Pay the whole
        // flow is successful. This avoids the Apple Pay sheet showing
        // success while the app is still waiting to create the session.
        const session = await createSession(
          transactionId,
          resolvedMinutes,
        );
        console.log("[Wallet] Hired session created", {
          transactionId,
          sessionId: session.sessionId,
          length: session.length,
        });
        completeWalletEvent("success");
        wheelDoneRef.current?.(true);
        // Now move SmartLockPage into its normal post-payment flow.
        // That flow sets LOADING, unlocks with the newly-created
        // session ID, then moves to UNLOCKED.
        await onPaymentSuccess?.({
          transactionId,
          sessionId: session.sessionId,
          length: session.length,
          minutes: session.length,
          totalCents: resolvedTotalCents,
          customers: customerCountValue,
        });
        console.log(
          "[Wallet] Post-payment flow completed",
        );
      } catch (paymentError) {
        // If we have not already told Apple Pay the payment succeeded,
        // mark the wallet event as failed.
        if (!eventCompleted) {
          completeWalletEvent("fail");
        }
        wheelDoneRef.current?.(false);
        console.error(
          "[Wallet] Payment flow failed:",
          paymentError,
        );
        setError(
          paymentError?.response?.data?.message ||
            paymentError?.response?.data?.error ||
            paymentError?.message ||
            "Payment succeeded, but we could not finish creating your session. Please contact support.",
        );
      } finally {
        walletProcessingRef.current = false;
        setPaying(false);
      }
    },
    [
      stripe,
      createPaymentIntent,
      createSession,
      onPaymentSuccess,
      customerCountValue,
    ],
  );
  useEffect(() => {
    handlePaymentMethodRef.current = handlePaymentMethod;
  }, [handlePaymentMethod]);

  const handlePaymentCancel = useCallback(() => {
    wheelDoneRef.current?.(false);
  }, []);
  useEffect(() => {
    if (!stripe) {
      return undefined;
    }
    if (typeof window === "undefined" || !window.isSecureContext) {
      return undefined;
    }
    const handleWalletPaymentMethod = (event) => {
      handlePaymentMethodRef.current?.(event);
    };

    const request = stripe.paymentRequest({
      country: "AU",
      currency: "aud",
      total: {
        label: itemTitle,
        amount: fixedSessionReady ? fixedSessionTotalCents : 100,
      },
      requestPayerName: false,
      requestPayerEmail: false,
      requestShipping: false,
    });
    let mounted = true;
    request
      .canMakePayment()
      .then((canMakePayment) => {
        if (!mounted) {
          return;
        }
        const apple = Boolean(canMakePayment?.applePay);
        const google = Boolean(canMakePayment?.googlePay);
        if (!apple && !google) {
          setWalletAvailable(false);
          setPaymentRequest(null);
          setWalletType(null);
          return;
        }
        setWalletAvailable(true);
        setPaymentRequest(request);
        setWalletType(apple ? "apple" : "google");
        request.on("paymentmethod", handleWalletPaymentMethod);
        request.on("cancel", handlePaymentCancel);
      })
      .catch(() => {
        if (!mounted) {
          return;
        }
        setWalletAvailable(false);
        setPaymentRequest(null);
        setWalletType(null);
      });
    return () => {
      mounted = false;
      try {
        request.off?.("paymentmethod", handleWalletPaymentMethod);
      } catch {
        // ignore
      }
      try {
        request.off?.("cancel", handlePaymentCancel);
      } catch {
        // ignore
      }
    };
  }, [
    stripe,
    itemTitle,
    handlePaymentCancel,
  ]);
  useEffect(() => {
    if (
      !paymentRequest ||
      !walletAvailable ||
      !fixedDurationMode ||
      !fixedSessionReady
    ) {
      return;
    }

    // The guest stepper changes fixedSessionTotalCents after the original
    // PaymentRequest has already been created. Update the live wallet
    // request so Apple Pay immediately reflects the current guest count.
    paymentRequest.update({
      total: {
        label: itemTitle,
        amount: fixedSessionTotalCents,
      },
    });
  }, [
    paymentRequest,
    walletAvailable,
    fixedDurationMode,
    fixedSessionReady,
    fixedSessionTotalCents,
    itemTitle,
  ]);

  const preparePayment = (minutes, totalCents, done = null) => {
    setError("");
    wheelDoneRef.current = done;
    selectedMinutesRef.current = minutes;
    totalCentsRef.current = totalCents;
    setHasSelection(true);
    if (paymentRequest && walletAvailable) {
      paymentRequest.update({
        total: {
          label: fixedDurationMode ? itemTitle : `Hire for ${minutes} min`,
          amount: totalCents,
        },
      });
      return;
    }
    // The timer wheel can stop its waiting state while the user
    // enters card details. We still retain the callback so a
    // successful card payment can complete the wheel flow.
    done?.(false);
  };
  const handleWheelPayment = (minutes, cost, done) => {
    const numericCost = Number(cost) || 0;
    const cents = Math.round(numericCost * 100);
    setSelectedMinutes(minutes);
    setSelectedCost(numericCost);
    preparePayment(minutes, cents, done);
  };
  const payWithCard = async () => {
    if (!stripe || !elements || paying) {
      return;
    }
    if (!hasSelection) {
      setError("Please select your hire length first.");
      return;
    }
    if (!cardComplete) {
      setError("Please complete your card details.");
      return;
    }
    setPaying(true);
    setError("");
    try {
      const minutes = selectedMinutesRef.current;
      const totalCents = totalCentsRef.current;
      const {
        clientSecret,
        transactionId,
        resolvedMinutes,
        resolvedTotalCents,
      } = await createPaymentIntent(
        minutes,
        totalCents,
      );
      const card = elements.getElement(CardElement);
      if (!card) {
        throw new Error("Card input not ready.");
      }
      const { error: confirmError } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card,
          },
        },
      );
      if (confirmError) {
        throw confirmError;
      }
      await completePayment(
        transactionId,
        resolvedMinutes,
        resolvedTotalCents,
      );
    } catch (paymentError) {
      wheelDoneRef.current?.(false);
      setError(
        paymentError?.response?.data?.message ||
          paymentError?.message ||
          "Card payment failed.",
      );
    } finally {
      setPaying(false);
    }
  };
  const displayMinutes = fixedDurationMode
    ? fixedSessionMinutes
    : selectedMinutes;
  const displayCost = fixedDurationMode
    ? fixedSessionCost
    : selectedCost;
  const paymentButtonLabel = hasSelection
    ? `Pay ${formatCurrency(displayCost)} & Continue`
    : "Select hire length first";
  return (
    <Box key="lock-session-hire" style={UI.screen}>
      <AmbientBackground />
      <Container size={560} px="md" style={UI.content}>
        <Box
          style={{
            ...UI.shell,
            paddingTop: 20,
            paddingBottom: 100,
          }}
        >
          {onCancel && (
            <Button
              variant="subtle"
              aria-label="Back"
              onClick={() => {
                setError("");
                wheelDoneRef.current?.(false);
                onCancel();
              }}
              style={UI.backButton}
            >
              <FaChevronLeft size={16} />
            </Button>
          )}
          <Flex direction="column" align="center" mt={onCancel ? 14 : 36}>
            <Title
              order={1}
              ta="center"
              style={{
                fontSize: "clamp(30px, 7vw, 36px)",
                lineHeight: 1.08,
                fontWeight: 800,
                letterSpacing: "-0.035em",
                color: "#171717",
              }}
            >
              Start your hire
            </Title>
            <Text
              c="dimmed"
              ta="center"
              mt={8}
              style={{
                fontSize: 15,
                fontWeight: 500,
              }}
            >
              {itemTitle}
            </Text>
            {chargePerSession ? (
              <Flex justify="center" gap={10} wrap="wrap" mt={20}>
                {/* {customerCostBase != fixedSessionCost && (
                  <Box style={UI.pill}>
                    <FaDollarSign size={14} color={APP_CLIP_ACCENT} />
                    <Text size="sm" fw={600}>
                      {formatCurrency(fixedSessionCost)}
                    </Text>
                  </Box>
                )} */}
                {!item.end_til_close_time && (
                  <Box style={UI.pill}>
                    <FaClock size={14} color={APP_CLIP_ACCENT} />
                    <Text size="sm" fw={600}>
                      {fixedSessionMinutes}{" "}
                      {fixedSessionMinutes === 1 ? "minute" : "minutes"}
                    </Text>
                  </Box>
                )}
              </Flex>
            ) : (
              <Text
                ta="center"
                mt={18}
                c="dimmed"
                style={{
                  maxWidth: 360,
                  lineHeight: 1.5,
                }}
              >
                Spin the wheel to choose how long you need access for.
              </Text>
            )}
          </Flex>
          <Box mt={24} style={UI.glassCard}>
            {hasCustomerCost && (
              <Box
                mb={18}
                // style={UI.innerCard}
              >
                <Flex align="center" gap={10} mb={10}>
                  <FaUser size={14} color={APP_CLIP_ACCENT} />
                  <Box>
                    <Text fw={700} size="sm">
                      Number of people
                    </Text>
                    <Text size="xs" c="dimmed" mt={2}>
                      {formatCurrency(customerCostBase)} per person
                      {chargePerSession ? "" : " / minute"}
                    </Text>
                  </Box>
                </Flex>
                <motion.div
                  initial={{
                    opacity: 0,
                    y: 8,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.3,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <Flex
                    align="center"
                    justify="space-between"
                    gap={14}
                    style={{
                      minHeight: 68,
                      padding: "8px",
                      borderRadius: 20,
                      background: "rgba(255, 255, 255, 0.74)",
                      border: "1px solid rgba(34, 139, 230, 0.10)",
                      boxShadow: "0 8px 22px rgba(15, 23, 42, 0.045)",
                    }}
                  >
                    <motion.div
                      whileTap={{
                        scale: 0.88,
                      }}
                      transition={{
                        duration: 0.1,
                      }}
                    >
                      <ActionIcon
                        variant="light"
                        color={customerCountValue <= 2 ? "blue" : "blue"}
                        size={52}
                        radius={16}
                        aria-label="Remove one person"
                        disabled={customerCountValue <= 1}
                        onClick={() => {
                          setCustomerCount(
                            String(Math.max(1, customerCountValue - 1)),
                          );
                        }}
                        style={{
                          border: "1px solid rgba(15, 23, 42, 0.06)",
                        }}
                      >
                        <FaMinus size={14} />
                      </ActionIcon>
                    </motion.div>
                    <Box
                      style={{
                        flex: 1,
                        textAlign: "center",
                        minWidth: 90,
                      }}
                    >
                      <motion.div
                        key={customerCountValue}
                        initial={{
                          opacity: 0,
                          y: 8,
                          scale: 0.9,
                        }}
                        animate={{
                          opacity: 1,
                          y: 0,
                          scale: 1,
                        }}
                        transition={{
                          duration: 0.2,
                          ease: "easeOut",
                        }}
                      >
                        <Text
                          fw={850}
                          style={{
                            fontSize: 28,
                            lineHeight: 1,
                            letterSpacing: "-0.035em",
                            color: "#171717",
                          }}
                        >
                          {customerCountValue}
                        </Text>
                      </motion.div>
                      <Text size="xs" c="dimmed" fw={650} mt={5}>
                        {customerCountValue === 1 ? "person" : "people"}
                      </Text>
                    </Box>
                    <motion.div
                      whileTap={{
                        scale: 0.88,
                      }}
                      transition={{
                        duration: 0.1,
                      }}
                    >
                      <ActionIcon
                        variant="light"
                        color="blue"
                        size={52}
                        radius={16}
                        aria-label="Add one person"
                        disabled={customerCountValue >= 99}
                        onClick={() => {
                          setCustomerCount(
                            String(Math.min(99, customerCountValue + 1)),
                          );
                        }}
                        style={{
                          border: "1px solid rgba(34, 139, 230, 0.10)",
                        }}
                      >
                        <FaPlus size={14} />
                      </ActionIcon>
                    </motion.div>
                  </Flex>
                </motion.div>
                <Flex
                  bg="grey"
                  opacity={0.25}
                  w="100%"
                  h={1}
                  my={"sm"}
                  mt={28}
                />
              </Box>
            )}
            {!fixedDurationMode && (
              <>
                <Center>
                  <Box
                    style={{
                      width: "100%",
                      maxWidth: 400,
                    }}
                  >
                    <TimerWheelView
                      config={{
                        costPerMinute: effectiveCostPerMinute,
                        maxMinutes,
                        allowsNegative: false,
                        snapDegree: 30,
                        ringLineWidth: 46,
                        ringGradient: {
                          colors: [
                            "rgba(34,139,230,0.30)",
                            "rgba(34,139,230,0.90)",
                          ],
                        },
                        shouldPlaySuccessHaptic: true,
                      }}
                      onRequestPayment={handleWheelPayment}
                    />
                  </Box>
                </Center>
                {selectedMinutes > 0 && (
                  <Box mt={18} style={UI.innerCard}>
                    <Flex justify="space-between" align="center" gap="md">
                      <Box>
                        <Text
                          size="xs"
                          c="dimmed"
                          fw={600}
                          tt="uppercase"
                          style={{
                            letterSpacing: "0.06em",
                          }}
                        >
                          Hire length
                        </Text>
                        <Text fw={700} mt={2}>
                          {selectedMinutes}{" "}
                          {selectedMinutes === 1 ? "minute" : "minutes"}
                        </Text>
                      </Box>
                      <Text
                        fw={800}
                        style={{
                          fontSize: 24,
                          letterSpacing: "-0.03em",
                        }}
                      >
                        {formatCurrency(selectedCost)}
                      </Text>
                    </Flex>
                  </Box>
                )}
              </>
            )}
            <Box mt={fixedDurationMode ? 0 : 24}>
              <Text
                fw={700}
                style={{
                  fontSize: 18,
                }}
              >
                Payment
              </Text>
              <Text size="sm" c="dimmed" mt={3}>
                Use a supported wallet or enter your card details below.
              </Text>
              {hasSelection && walletAvailable && paymentRequest && (
                <Box mt={18}>
                  <Text
                    size="xs"
                    c="dimmed"
                    fw={700}
                    tt="uppercase"
                    mb={8}
                    style={{
                      letterSpacing: "0.06em",
                    }}
                  >
                    {walletType === "apple" ? "Apple Pay" : "Google Pay"}
                  </Text>
                  <Box
                    className="slb-wallet-button"
                    style={{
                      width: "100%",
                    }}
                  >
                    <PaymentRequestButtonElement
                      options={{
                        paymentRequest,
                        style: {
                          paymentRequestButton: {
                            type: "buy",
                            theme: "dark",
                            height: "54px",
                          },
                        },
                      }}
                    />
                  </Box>
                  <Flex align="center" gap={12} my={18}>
                    <Box
                      style={{
                        height: 1,
                        flex: 1,
                        background: "rgba(15, 23, 42, 0.09)",
                      }}
                    />
                    <Text size="xs" c="dimmed" fw={600}>
                      OR PAY WITH CARD
                    </Text>
                    <Box
                      style={{
                        height: 1,
                        flex: 1,
                        background: "rgba(15, 23, 42, 0.09)",
                      }}
                    />
                  </Flex>
                </Box>
              )}
              <Box
                mt={hasSelection && walletAvailable && paymentRequest ? 0 : 18}
              >
                <Flex align="center" gap={8} mb={8}>
                  <FaCreditCard size={14} color={APP_CLIP_ACCENT} />
                  <Text size="sm" fw={700}>
                    Card details
                  </Text>
                </Flex>
                <Box className="slb-card-element" style={UI.cardElement}>
                  <CardElement
                    options={cardElementOptions}
                    onChange={(event) => {
                      setCardComplete(event.complete);
                      if (event.error) {
                        setError(
                          event.error.message ||
                            "Please check your card details.",
                        );
                      } else if (error && event.complete) {
                        setError("");
                      }
                    }}
                  />
                </Box>
                {!hasSelection && !fixedDurationMode && (
                  <Text size="xs" c="dimmed" mt={8}>
                    Select your hire length above to enable payment.
                  </Text>
                )}
                <Button
                  fullWidth
                  mt={16}
                  color={APP_CLIP_ACCENT}
                  loading={paying}
                  disabled={!hasSelection || !cardComplete || paying}
                  onClick={payWithCard}
                  style={UI.primaryButton}
                >
                  {paymentButtonLabel}
                </Button>
              </Box>
            </Box>
          </Box>
          {error && (
            <Alert
              mt={16}
              color="red"
              variant="light"
              radius={18}
              style={{
                width: "100%",
                background: "rgba(255,255,255,0.72)",
                border: "1px solid rgba(224,49,49,0.14)",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
              }}
            >
              {error}
            </Alert>
          )}
        </Box>
      </Container>
    </Box>
  );
}
